import type { Lesson } from "@/core/types";
import { criterion, fromCriteria } from "@/core/check";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_ANIMATIONEN } from "../makros";

export const lektionAnimationen: Lesson = {
  id: "animationen",
  title: "Animationen",
  summary: "Vier Arten, ein Animationsbereich – und eine Frage, die vorher zu klären ist.",
  durationMinutes: 9,
  learningGoals: [
    "Die vier Animationsarten unterscheiden",
    "Den Animationsbereich zum Ordnen der Abfolge nutzen",
    "Animationen nur dort einsetzen, wo sie etwas erklären",
  ],
  steps: [
    {
      id: "vier-arten",
      title: "Die vier Arten",
      explanation: [
        "Auf der Registerkarte „Animationen“ sind die Effekte nach ihrer Wirkung " +
          "gruppiert und farblich unterschieden. Eingangseffekte (grün) lassen ein " +
          "Objekt erscheinen. Hervorhebungseffekte (gelb) verändern ein sichtbares " +
          "Objekt, etwa durch Vergrößern oder einen Farbwechsel. Ausgangseffekte (rot) " +
          "lassen es verschwinden. Animationspfade (grau) bewegen es entlang einer Linie.",
        "Für den Alltag reichen zwei Effekte: „Erscheinen“ und „Verblassen“. Beide sind " +
          "unauffällig und tun genau das, was ein Eingangseffekt tun soll – den " +
          "Zeitpunkt steuern, zu dem etwas sichtbar wird.",
        "Der häufigste sinnvolle Einsatz ist die Aufzählung, die punktweise erscheint. " +
          "Sie hält die Aufmerksamkeit beim aktuellen Punkt, statt dem Publikum sofort " +
          "alle sechs Stichpunkte zum Vorauslesen anzubieten. Eingestellt wird das über " +
          "„Animationen“ → Effekt wählen → „Effektoptionen“ → „Nach Absatz“.",
        "Der zweite sinnvolle Einsatz ist ein Ablauf, bei dem die Reihenfolge zur Sache " +
          "gehört: Pfeile in einem Prozessdiagramm, die nacheinander erscheinen, oder " +
          "eine Zahl, die erst nach der Frage auftaucht.",
      ],
      keyPoints: [
        "Grün = Eingang, Gelb = Hervorhebung, Rot = Ausgang, Grau = Pfad.",
        "„Erscheinen“ und „Verblassen“ genügen für fast alles.",
        "Effektoptionen → „Nach Absatz“ lässt eine Aufzählung punktweise erscheinen.",
      ],
      task: {
        instruction:
          "Lass die Stichpunkte einer Aufzählung mit dem Effekt „Erscheinen“ oder „Verblassen“ " +
          "nacheinander erscheinen – Effektoptionen „Nach Absatz“.",
        hint: "Textplatzhalter anklicken, Animationen → Verblassen → Effektoptionen → Nach Absatz.",
        macro: MAKRO_ANIMATIONEN,
        check: () =>
          pruefeMitMakro(MAKRO_ANIMATIONEN, (bericht) => {
            const folien = [...bericht.gruppe("folie").entries()];
            const absatzweise = folien.filter(([, folie]) => Number(folie.get("nachAbsatz") ?? 0) >= 2);
            return fromCriteria(
              [
                criterion(
                  absatzweise[0]
                    ? `Folie ${absatzweise[0][0]}: ${absatzweise[0][1].get("nachAbsatz")} Absätze erscheinen einzeln`
                    : "Eine Aufzählung erscheint absatzweise",
                  absatzweise.length > 0,
                ),
              ],
              {
                success: "Die Stichpunkte erscheinen einzeln – das Publikum bleibt beim aktuellen Punkt.",
                failure: "Es gibt noch keine Aufzählung, die absatzweise erscheint.",
                hint: "Die Animation muss am Textplatzhalter sitzen, nicht an einer Form daneben.",
              },
            );
          }),
      },
    },
    {
      id: "animationsbereich",
      title: "Der Animationsbereich",
      explanation: [
        "Sobald mehr als zwei Animationen auf einer Folie liegen, wird die Reihenfolge " +
          "unübersichtlich. Dafür gibt es den Animationsbereich: „Animationen“ → " +
          "„Animationsbereich“. Er listet alle Effekte der Folie in ihrer Abfolge und " +
          "lässt sie per Ziehen umsortieren.",
        "Die drei Startarten stehen daneben unter „Anzeigedauer“. „Beim Klicken“ wartet " +
          "auf einen Mausklick. „Mit vorheriger“ startet gleichzeitig mit dem Effekt " +
          "davor. „Nach vorheriger“ startet, sobald der vorherige fertig ist – das ist " +
          "die Einstellung für einen Ablauf, der von selbst durchläuft.",
        "Ein praktischer Hinweis: Die Zahlen, die neben den Objekten auf der Folie " +
          "erscheinen, sind die Klickreihenfolge. Zwei Objekte mit derselben Zahl starten " +
          "gemeinsam. Objekte ganz ohne Zahl haben keine Animation.",
        "Über die Schaltfläche „Animation übertragen“ – das Pinselsymbol – lässt sich " +
          "ein fertig eingestellter Effekt samt Anzeigedauer auf ein anderes Objekt " +
          "übertragen. Das spart bei gleichartigen Folien viel Zeit.",
      ],
      keyPoints: [
        "Animationen → Animationsbereich zeigt und sortiert die Abfolge.",
        "Beim Klicken, Mit vorheriger, Nach vorheriger – drei Startarten.",
        "„Animation übertragen“ kopiert einen eingestellten Effekt auf ein anderes Objekt.",
      ],
      task: {
        instruction:
          "Baue auf einer Folie einen kleinen Ablauf aus mindestens zwei Animationen, von " +
          "denen mindestens eine „Mit vorheriger“ oder „Nach vorheriger“ startet.",
        hint: "Zwei Formen animieren, Animationsbereich öffnen, beim zweiten Effekt unter „Start“ „Nach vorheriger“ wählen.",
        macro: MAKRO_ANIMATIONEN,
        check: () =>
          pruefeMitMakro(MAKRO_ANIMATIONEN, (bericht) => {
            const folien = [...bericht.gruppe("folie").entries()];
            const ablauf = folien.filter(
              ([, folie]) =>
                Number(folie.get("effekte") ?? 0) >= 2 &&
                Number(folie.get("mitVorheriger") ?? 0) + Number(folie.get("nachVorheriger") ?? 0) > 0,
            );
            return fromCriteria(
              [
                criterion(
                  ablauf[0] ? `Folie ${ablauf[0][0]}: ${ablauf[0][1].get("effekte")} Effekte im Ablauf` : "Eine Folie mit einem Ablauf aus mehreren Effekten",
                  ablauf.length > 0,
                ),
              ],
              {
                success: "Der Ablauf läuft von selbst weiter, sobald du ihn startest.",
                failure: "Es gibt noch keine Folie mit einem Ablauf aus mehreren Effekten.",
              },
            );
          }),
      },
    },
    {
      id: "sparsam",
      title: "Die Frage vor der Animation",
      explanation: [
        "Vor jeder Animation lohnt sich eine Frage: Was versteht das Publikum dadurch " +
          "besser? Wenn die Antwort „nichts, es sieht lebendiger aus“ lautet, ist die " +
          "Animation überflüssig – und kostet Zeit, Aufmerksamkeit und Robustheit.",
        "Robustheit deshalb, weil Animationen die häufigste Ursache für Probleme beim " +
          "Präsentieren auf fremden Geräten sind. In älteren PowerPoint-Versionen sehen " +
          "manche Effekte anders aus, in PowerPoint im Web fehlen einige ganz, und in " +
          "einer PDF-Fassung sind alle weg. Eine Folie, die erst mit Animation " +
          "verständlich ist, ist als PDF unverständlich.",
        "Daraus folgt eine praktische Regel: Jede Folie muss auch ohne Animation " +
          "funktionieren. Die Animation steuert dann nur, wann etwas sichtbar wird – " +
          "nicht, ob es überhaupt Sinn ergibt.",
        "Und eine letzte Überlegung zur Barrierefreiheit: Bewegung kann bei manchen " +
          "Menschen Übelkeit oder Schwindel auslösen; Windows und macOS haben dafür eine " +
          "Systemeinstellung, Animationen zu reduzieren. Wer Animationen sparsam " +
          "einsetzt, umgeht das Thema von selbst.",
      ],
      keyPoints: [
        "Testfrage: Was versteht das Publikum durch diese Animation besser?",
        "Jede Folie muss auch ohne Animation verständlich sein – Stichwort PDF.",
        "Viel Bewegung ist für manche Menschen körperlich unangenehm.",
      ],
    },
  ],
};
